import { ReactElement, Suspense, useEffect, useState } from 'react';
import { useSuspenseQuery } from '@apollo/client/react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Button,
  ContextualTablePagination,
  FullLoader,
  Pill,
  TableColumns,
  TableContainer,
  TableRows,
  useTableContext,
} from '@sc/shared-ui';
import {
  ArrowPathIcon,
  ArrowUpIcon,
  CalendarIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { InvoiceStatus } from '@sc/graphql';
import { GQL_QUERY_LIST_INVOICES } from '../utility/queries';
import { InvoiceDetailDrawer } from '../modals/InvoiceDetailDrawer';
import { PayInvoiceModal } from '../components/PayInvoice';
import { planDisplayName } from '@sc/stripe/pricing';
import { planIcon } from '../utility/planDisplay';

const formatDate = (iso: string): string =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));

const formatGbp = (
  value: number | null | undefined,
  currency: string
): string => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(value);
};

const STATUS_PILL: Record<InvoiceStatus, { text: string; colour: string }> = {
  [InvoiceStatus.paid]: { text: 'Paid', colour: '#3aa16e' },
  [InvoiceStatus.open]: { text: 'Open', colour: '#da892b' },
  [InvoiceStatus.draft]: { text: 'Draft', colour: '#5f656d' },
  [InvoiceStatus.uncollectible]: { text: 'Late', colour: '#cb3b3b' },
  [InvoiceStatus.void]: { text: 'Void', colour: '#5f656d' },
};

const descriptionIcon = (description: string) => {
  const lower = description.toLowerCase();

  if (lower.includes('auto top'))
    return <ArrowPathIcon className="size-4 text-text-low-priority" />;

  if (lower.includes('top') || lower.includes('credit'))
    return <ArrowUpIcon className="size-4 text-text-low-priority" />;

  return (
    <PaperAirplaneIcon className="size-4 text-text-low-priority -rotate-90" />
  );
};

const InvoiceTableRows = ({
  onView,
  onPay,
}: {
  onView: (uuid: string) => void;
  onPay: (invoice: { uuid: string; total: number | null }) => void;
}): ReactElement => {
  const { setTotalPages, page, rowsPerPage } = useTableContext();

  const { data } = useSuspenseQuery(GQL_QUERY_LIST_INVOICES, {
    variables: {
      input: {
        pagination: {
          page,
          perPage: rowsPerPage,
        },
      },
    },
  });

  useEffect(() => {
    setTotalPages(data.listInvoices.pagination.lastPage);
  }, [data, setTotalPages]);

  return (
    <TableRows
      widthType="pc"
      rows={data.listInvoices.items.map((invoice) => {
        const status = STATUS_PILL[invoice.status];

        return {
          uuid: invoice.uuid,
          cells: [
            {
              width: 25,
              content: (
                <div className="flex items-center gap-2">
                  <CalendarIcon className="size-4 text-text-low-priority shrink-0" />
                  <span className="text-sm text-white">
                    {formatDate(invoice.createdAt)}
                  </span>
                </div>
              ),
            },
            {
              width: 30,
              content: (
                <div className="flex items-center gap-2 min-w-0">
                  {invoice.subscriptionType
                    ? planIcon(
                        invoice.subscriptionType,
                        'size-4 text-text-low-priority shrink-0'
                      )
                    : descriptionIcon(invoice.description)}
                  <span className="text-sm text-white truncate">
                    {invoice.subscriptionType
                      ? `${planDisplayName(invoice.subscriptionType)} monthly plan`
                      : invoice.description}
                  </span>
                </div>
              ),
            },
            {
              width: 15,
              content: (
                <Pill
                  size="small"
                  outline
                  colour={status.colour}
                  text={status.text}
                />
              ),
            },
            {
              width: 15,
              content: (
                <span className="text-sm text-primary-400 font-semibold">
                  {formatGbp(invoice.total, invoice.currency)}
                </span>
              ),
            },
            {
              width: 15,
              wrapperClassname: 'justify-end',
              content: (
                <div className="flex items-center gap-2">
                  {(invoice.status === InvoiceStatus.open ||
                    invoice.status === InvoiceStatus.uncollectible) && (
                    <Button
                      button={{
                        text: 'Pay now',
                        size: 'small',
                        type: 'primary',
                        shrink: true,
                        onClick: () =>
                          onPay({
                            uuid: invoice.uuid,
                            total: invoice.total,
                          }),
                      }}
                    />
                  )}
                  <Button
                    button={{
                      text: 'View',
                      size: 'small',
                      type: 'low-priority',
                      shrink: true,
                      onClick: () => onView(invoice.uuid),
                    }}
                  />
                </div>
              ),
            },
          ],
        };
      })}
    />
  );
};

export const BillingInvoicesTab = (): ReactElement => {
  
  const { invoice } = useSearch({ from: '/_authenticated/billing' });
  const navigate = useNavigate();

  const setOpenUuid = (uuid: string | null) =>
    void navigate({
      to: '/billing',
      search: (prev) => ({ ...prev, invoice: uuid ?? undefined }),
      hash: '2',
    });

  const [payInvoice, setPayInvoice] = useState<{
    uuid: string;
    total: number | null;
  } | null>(null);

  return (
    <>
      <TableContainer>
        <TableColumns
          widthType="pc"
          columns={[
            { width: 25, heading: 'Date' },
            { width: 30, heading: 'Description' },
            { width: 15, heading: 'Status' },
            { width: 15, heading: 'Total' },
            { width: 15 },
          ]}
        />
        <Suspense fallback={<FullLoader className="size-6" />}>
          <InvoiceTableRows onView={setOpenUuid} onPay={setPayInvoice} />
        </Suspense>
        <ContextualTablePagination />
      </TableContainer>
      <InvoiceDetailDrawer
        open={!!invoice}
        uuid={invoice ?? null}
        onClose={() => setOpenUuid(null)}
      />
      {payInvoice && (
        <PayInvoiceModal
          open
          invoiceUuid={payInvoice.uuid}
          total={payInvoice.total}
          onClose={() => setPayInvoice(null)}
        />
      )}
    </>
  );
};
